/**
 * WorldScriptRuntimeModule — runs each world's CLIENT-side Lua on every visitor
 * (workstream 7: "do whatever in your world" — games, digital twins).
 *
 * ARCHITECTURE (the Roblox local-script analogue, inverted trust):
 *  - `world_script` rows replicate a world's Lua SOURCE to every client in it.
 *  - Each client executes them in the EngineX **sandboxed Wasmoon runtime**
 *    (read-only stdlib, isolated per-script envs, injection-safe loading, a
 *    3-strike error budget that auto-disables a faulting script).
 *  - Scripts are EXPERIENTIAL: they spawn/animate props, write labels, read
 *    players/terrain/time, and react to replicated CONTRACT events. Authority
 *    never moves here — shared state still changes only through the gated
 *    reducers/contracts, so a hostile script can draw things, not own things.
 *  - The bridge below is the deliberate capability surface. It builds on
 *    engine subsystems but exposes a curated API — handing visiting clients
 *    the raw engine globals would let a world script drive ANY subsystem on
 *    YOUR machine, which is why "all of EngineX" is scoped to this surface
 *    (extended per need) rather than exposed wholesale.
 *
 * Script shape (the ENGINE lifecycle convention — return a table of hooks):
 *   return {
 *     on_init  = function() ... end,
 *     on_tick  = function(dt) ... end,
 *     on_event = function(type, payload) ... end,  -- replicated contract events
 *   }
 *
 * Bridge (wasmoon collapses JS objects, so returns are FLAT values):
 *   world.spawn(prefabKey, x, y, z) → handle · world.set(h,x,y,z[,yaw[,scale]])
 *   world.remove(h) · world.label(text,x,y,z) → handle (world.set moves it)
 *   world.player_count() · world.player_name(i)/player_x(i)/player_y(i)/player_z(i)
 *   world.my_name()/my_x()/my_y()/my_z() (the LOCAL player — proximity gameplay)
 *   world.tween(h,x,y,z,seconds) — smooth prop motion (doors/elevators/patrols)
 *   world.particles(x,y,z[,count[,hexColor[,speed]]]) — one-shot burst
 *   world.tone(freq,ms[,vol]) — clamped WebAudio beep (games need feedback)
 *   world.terrain_height(x,z) · world.time_of_day() · world.log(msg)
 *
 * v3 SURFACE (the "full EngineX reach" wave — still curated + budgeted):
 *   primitives  box/sphere (no prefab needed) · light (PointLight, capped)
 *   prop FX     color/opacity/spin/scale_to/look_at · attach/detach to parts
 *   environment fog(hex,density)/fog_clear — restored on teardown
 *   queries     raycast · dist · part_* accessors (the build-object bridge) ·
 *               part_set/part_reset (LOCAL visual override — server row untouched) ·
 *               npc_* accessors · cam_* · key_down · is_owner/world_name/world_size · time
 *   UI          hud(text) per-script overlay line · toast(msg) rate-limited
 *   storage     store_set/store_get — per-world per-player localStorage (capped)
 *
 * PREVIEW RUN (the IDE ▶ Run loop — UEFN "push changes" analogue): the bus
 * events `atlas:script-preview`/`-stop` load a buffer into a LOCAL-ONLY slot
 * without touching the server — instant test, only this client sees it.
 */

import * as THREE from 'three/webgpu';
import { isGameAudioMuted } from '../lib/audio_mute.js';
import { audioRuntime } from '../lib/audio/runtime.js';
import type { App, IModule } from 'enginex';
import { LuaRuntime } from 'enginex/scripting/LuaRuntime.js';
import { Logger } from 'enginex/core/Logger.js';
import { disposeLater } from '../lib/gpu_dispose.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { onInteract } from '../lib/interact_input.js';
import { lookAtLocal } from '../lib/local_origin.js';

/** Scratch for a script's look_at target (lookAtLocal overwrites it). */
const LOOK_TARGET = new THREE.Vector3();
import { Phase, makeSystem } from '../engine-compat.js';
import { terrainHeight } from '@shared/terrain.js';
import type { AssetModule } from './AssetModule.js';
import type { FreeformBuildModule } from './FreeformBuildModule.js';
import type { NpcUnitModule, NpcUnit } from './NpcUnitModule.js';
import type { DeviceModule } from './DeviceModule.js';

// Scratch vectors for the script raycast API (callable every on_tick).
const RAY_ORIGIN = new THREE.Vector3();
const RAY_DIR = new THREE.Vector3();
const MAX_PROPS_PER_SCRIPT = 256;
const MAX_BURSTS_PER_SCRIPT = 12;
const MAX_BURST_PARTICLES = 400;
const MAX_TONES = 4; // concurrent oscillators, volume/duration clamped
const MAX_LIGHTS_PER_SCRIPT = 8; // real PointLights are a render-cost budget
const MAX_STORE_KEYS = 64;
const MAX_STORE_VALUE = 256;
const INTERACT_RANGE_M = 3.5; // on_interact reach (E beside a part)
const MAX_PUMP_MS = 100; // a single tick over this counts as a strike (soft watchdog)
const MAX_STRIKES = 3;
const PREVIEW_KEY = '::preview';
const MAX_SIGNALS_PER_FRAME = 16; // npc.signal fan-out budget
const NPC_SYNC_FALLBACK_MS = 2000; // wall-clock resync even when the host signature is unchanged (a preview handing a slot back)

interface ScriptSlot {
  id: number; // runtime entity id
  rowKey: string;
  name: string;
  strikes: number;
  props: Map<number, { obj: THREE.Object3D; assetId: bigint | null }>;
  nextHandle: number;
  lights: number;
  lastToastAt: number;
  /** Set for an NPC creator slot: the npc_unit this script animates. */
  npcId?: string;
}

export class WorldScriptRuntimeModule implements IModule {
  readonly name = 'WorldScriptRuntime';

  private scene!: THREE.Scene;
  private runtime: LuaRuntime | null = null;
  private runtimeReady = false;
  private slots = new Map<string, ScriptSlot>(); // rowKey → slot
  private nextId = 1;
  private root = new THREE.Group();
  private eventQueue: Array<{ type: string; payload: string }> = [];
  private seed = '';
  private worldSig = ''; // current (worldId + script rows) signature — rebuild detector
  private activeSlot: ScriptSlot | null = null; // strike attribution during a pump
  // Module-side animators (advanced in pump, cleared on teardown)
  private tweens = new Map<string, { obj: THREE.Object3D; from: THREE.Vector3; to: THREE.Vector3; t0: number; dur: number }>();
  private bursts: Array<{ points: THREE.Points; vel: Float32Array; born: number; life: number; slot: ScriptSlot }> = [];
  private liveTones = 0;
  // v3 animators + environment/UI state
  private spins = new Map<string, { obj: THREE.Object3D; rate: number }>();
  private scaleTweens = new Map<string, { obj: THREE.Object3D; from: number; to: number; t0: number; dur: number }>();
  private fogSaved: { fog: THREE.Scene['fog']; had: boolean } | null = null;
  // The ONE script-owned fog object — set_fog mutates it in place (fog law).
  private scriptFog: THREE.FogExp2 | null = null;
  private hudEl: HTMLDivElement | null = null;
  private hudLines = new Map<string, string>(); // slot rowKey → HUD line
  private keys = new Set<string>(); // lowercase KeyboardEvent.key state
  private partOverrides = new Set<string>(); // part ids with a live local visual override
  private bornAt = performance.now(); // world.time() epoch (reset per load wave)
  private raycaster = new THREE.Raycaster();
  // NPC creator slots: per-unit script signatures + the npc.signal queue.
  private npcSlotSig = new Map<string, string>();
  private lastNpcSig = '';
  private lastNpcSyncAt = 0;
  private lastNpcVersion = -1;
  private signalQueue: Array<{ topic: string; payload: string; from: string }> = [];

  constructor(private readonly assets: AssetModule, private readonly parts?: FreeformBuildModule, private readonly npcs?: NpcUnitModule, private readonly devices?: DeviceModule) {}

  init(app: App): void {
    this.scene = app.world.scene;
    this.scene.add(this.root);

    bus.on('atlas:account-ready', () => this.bind());
    bus.on('atlas:world-cleared', () => void this.teardownAll());
    bus.on('atlas:script-preview', ({ name, source, npcId, events }) => void this.runPreview(name, source, npcId, events));
    bus.on('atlas:script-preview-stop', () => this.stopPreview());
    // The world SIGNAL bus: devices, NPC dialogue actions and the console post
    // here; scripts receive them as on_signal. Script-origin signals are queued
    // directly by signal() (and mirrored to the bus for devices) — skip them.
    bus.on('atlas:signal', (sg) => {
      if (sg.from.startsWith('script:')) return;
      if (this.signalQueue.length >= MAX_SIGNALS_PER_FRAME * 4) return;
      this.signalQueue.push({ topic: sg.topic, payload: sg.payload, from: sg.from });
    });
    // The engine's Lua runtime reports hook errors through its Logger — mirror
    // them into the IDE console (and the preview report) with the script's name,
    // so "what happened when it ran" includes the Lua error, not just silence.
    Logger.onLog((level, tag, msg, args) => {
      if (tag !== 'Lua' || level < 2) return;
      const text = [msg, ...args.map((a) => (a instanceof Error ? a.message : typeof a === 'string' ? a : ''))].filter(Boolean).join(' ');
      const slot = this.activeSlot;
      bus.emit('atlas:script-log', { script: slot?.name ?? 'lua', msg: text.slice(0, 400), level: level >= 3 ? 'error' : 'info' });
      if (slot?.rowKey === PREVIEW_KEY && level >= 3) this.previewErrors++;
    });
    // key_down() state — ignored while the user types in an input/editor.
    window.addEventListener('keydown', (e) => {
      const t = e.target instanceof Element ? (e.target as HTMLElement) : null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.closest('.cm-editor'))) return;
      this.keys.add(e.key.toLowerCase());
    });
    // on_interact: Interact (edge, not held) next to a build part fires the hook with that part's id — the event the
    // Edit Mode "Script" tool targets. It heard the PHYSICAL E only: a pad, touch or rebound Interact never fired it.
    onInteract(() => { if (!atlas.editMode) this.queueInteract(); });
    window.addEventListener('keyup', (e) => this.keys.delete(e.key.toLowerCase()));
    window.addEventListener('blur', () => this.keys.clear());
    app.world.addSystem(makeSystem('WorldScriptPump', Phase.Animation, 50, (dt) => this.pump(dt)));
    // Probe surface (verify:edit-mode:probe): what the runtime believes about its slots.
    (window as unknown as Record<string, unknown>).__scripts = {
      state: () => ({ ready: this.runtimeReady, hasRuntime: !!this.runtime, slots: [...this.slots.keys()], npcSig: [...this.npcSlotSig.keys()], npcsWired: !!this.npcs, lastSyncError: this.lastSyncError }),
    };
  }

  private bind(): void {
    const conn = atlas.conn;
    if (!conn) return;
    // Own subscription handle: an un-published server (no world_script table yet)
    // rejects only this, leaving everything else intact.
    try {
      conn.subscriptionBuilder().subscribe(['SELECT * FROM world_script']);
    } catch { /* pre-publish server — world scripts are simply absent */ }
    const tbl = (conn.db as unknown as { worldScript?: {
      onInsert(cb: () => void): void; onUpdate(cb: () => void): void; onDelete(cb: () => void): void;
    } }).worldScript;
    if (tbl) {
      const resync = (): void => void this.syncScripts();
      tbl.onInsert(resync);
      tbl.onUpdate(resync);
      tbl.onDelete(resync);
    }
    // Replicated contract events fan into on_event (the server→visuals bridge).
    conn.db.contractEvent.onInsert((_c, row) => {
      if (row.worldId === atlas.currentWorldId) {
        this.eventQueue.push({ type: row.type, payload: row.payload });
        if (this.eventQueue.length > 64) this.eventQueue.shift(); // backpressure
      }
    });
    bus.on('atlas:world-entered', () => void this.syncScripts());
  }

  // ─── Script lifecycle ─────────────────────────────────────────────────────────

  /** (Re)load the CURRENT world's enabled scripts when rows or the world change. */
  private async syncScripts(): Promise<void> {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return;
    const world = conn.db.world.worldId.find(wid);
    this.seed = world?.terrainSeed ?? '';

    interface Row { scriptId: bigint; worldId: bigint; name: string; source: string; enabled: boolean; updatedAt: { microsSinceUnixEpoch: bigint } }
    const tbl = (conn.db as unknown as { worldScript?: { iter(): Iterable<Row> } }).worldScript;
    if (!tbl) return;
    const rows: Row[] = [];
    for (const r of tbl.iter()) if (r.worldId === wid && r.enabled) rows.push(r);
    rows.sort((a, b) => (a.scriptId < b.scriptId ? -1 : 1));

    const sig = `${wid}|` + rows.map((r) => `${r.scriptId}:${r.updatedAt.microsSinceUnixEpoch}`).join(',');
    if (sig === this.worldSig) return; // nothing changed
    this.worldSig = sig;

    await this.teardownAll(sig);
    this.bornAt = performance.now();
    if (rows.length === 0) return;

    await this.ensureRuntime();
    for (const r of rows) {
      await this.loadSlot(r.scriptId.toString(), r.name, r.source);
    }
    if (this.slots.size > 0) {
      bus.emit('atlas:status', { text: `${this.slots.size} world script(s) running (sandboxed).` });
    }
  }

  private runtimeInit: Promise<LuaRuntime> | null = null; // single-flight: two concurrent callers must not each boot a WASM VM
  private lastSyncError = '';
  private async ensureRuntime(): Promise<LuaRuntime> {
    if (this.runtime) return this.runtime;
    if (!this.runtimeInit) {
      this.runtimeInit = (async () => {
        const rt = new LuaRuntime();
        await rt.init();
        this.installBridge(rt);
        this.runtime = rt;
        this.runtimeReady = true;
        return rt;
      })().catch((e: unknown) => { this.runtimeInit = null; throw e; });
    }
    return this.runtimeInit;
  }

  private async loadSlot(rowKey: string, name: string, source: string, npcId?: string): Promise<boolean> {
    const rt = await this.ensureRuntime();
    const slot: ScriptSlot = {
      id: this.nextId++,
      rowKey,
      name,
      strikes: 0,
      props: new Map(),
      nextHandle: 1,
      lights: 0,
      lastToastAt: 0,
      npcId,
    };
    const ok = await rt.loadScript(slot.id, source, `world-script/${name}.lua`);
    if (!ok) {
      bus.emit('atlas:status', { text: `World script "${name}" failed to load (see console).` });
      bus.emit('atlas:script-log', { script: name, msg: 'failed to load (syntax error — see devtools console)', level: 'error' });
      return false;
    }
    this.slots.set(slot.rowKey, slot);
    this.activeSlot = slot;
    try { this.call(slot, 'on_init'); } finally { this.activeSlot = null; }
    return true;
  }

  /** callHook + the preview report's per-hook counter. */
  private call(slot: ScriptSlot, hook: string, ...args: unknown[]): void {
    if (!this.runtime) return;
    this.runtime.callHook(slot.id, hook, ...args);
    if (slot.rowKey === PREVIEW_KEY) this.previewHooks[hook] = (this.previewHooks[hook] ?? 0) + 1;
  }

  // ─── Preview runs (the IDE ▶ Run loop — local-only, never persisted) ─────────

  private previewNpcId: string | null = null; // an NPC whose LIVE slot yields to the preview
  // Preview test report: hook call counts + hook errors + synthetic-event timers.
  private previewHooks: Record<string, number> = {};
  private previewErrors = 0;
  private previewT0 = 0;
  private previewTimers: number[] = [];
  private previewGen = 0; // bumps per runPreview; a stop during load cancels that load

  private async runPreview(name: string, source: string, npcId?: string, events?: Array<{ at: number; hook: string; args: unknown[] }>): Promise<void> {
    if (atlas.currentWorldId === null) return;
    this.stopPreview();
    this.previewHooks = {};
    this.previewErrors = 0;
    this.previewT0 = performance.now();
    const gen = ++this.previewGen;
    if (npcId) {
      // The unit's live slot stands down so the preview alone drains its senses.
      const live = this.slots.get(`npc:${npcId}`);
      if (live) { this.unloadSlot(live); this.slots.delete(live.rowKey); }
      this.npcSlotSig.delete(npcId);
      this.previewNpcId = npcId;
    }
    const label = `preview:${name}`;
    const ok = await this.loadSlot(PREVIEW_KEY, label, source, npcId);
    if (gen !== this.previewGen) {
      // Stopped (or restarted) while the Lua was compiling — drop this load so it never runs orphaned.
      const orphan = this.slots.get(PREVIEW_KEY);
      if (orphan && orphan.name === label && ok) { this.unloadSlot(orphan); this.slots.delete(PREVIEW_KEY); }
      return;
    }
    if (!ok) { this.previewErrors++; return; }
    bus.emit('atlas:script-log', { script: label, msg: '▶ preview running (only you see it — Deploy publishes to everyone)', level: 'info' });
    // Synthetic events (the IDE / copilot test harness): "a player walked up at
    // 0.5 s, said hello at 1 s" — delivered through the same paths real events take.
    for (const ev of events ?? []) {
      if (!ev || typeof ev.hook !== 'string' || !/^on_[a-z_]+$/.test(ev.hook)) continue;
      const args = Array.isArray(ev.args) ? ev.args.slice(0, 6) : [];
      const t = window.setTimeout(() => {
        const slot = this.slots.get(PREVIEW_KEY);
        if (!slot || slot.name !== label) return;
        bus.emit('atlas:script-log', { script: label, msg: `⇢ synthetic ${ev.hook}(${args.map((a) => JSON.stringify(a)).join(', ')})`, level: 'info' });
        if (slot.npcId) { this.npcs?.get(slot.npcId)?.push(ev.hook, ...args); return; }
        this.activeSlot = slot;
        try { this.call(slot, ev.hook, ...args); } finally { this.activeSlot = null; }
      }, Math.max(0, Math.min(60_000, (Number(ev.at) || 0) * 1000)));
      this.previewTimers.push(t);
    }
  }

  private stopPreview(): void {
    for (const t of this.previewTimers) window.clearTimeout(t);
    this.previewTimers = [];
    const npcId = this.previewNpcId ?? undefined;
    this.previewNpcId = null; // the next sync reloads the unit's live slot
    const started = this.previewT0 > 0;
    this.previewGen++; // cancels an in-flight load
    const slot = this.slots.get(PREVIEW_KEY);
    if (slot) { this.unloadSlot(slot); this.slots.delete(PREVIEW_KEY); }
    if (!started) return;
    const seconds = Math.round((performance.now() - this.previewT0) / 100) / 10;
    this.previewT0 = 0;
    const name = slot?.name ?? 'preview';
    bus.emit('atlas:script-log', { script: name, msg: `■ preview stopped after ${seconds}s — hooks: ${Object.entries(this.previewHooks).map(([h, n]) => `${h}×${n}`).join(' ') || 'none'}; errors: ${this.previewErrors}`, level: this.previewErrors ? 'error' : 'info' });
    bus.emit('atlas:script-preview-stats', { name, hooks: { ...this.previewHooks }, errors: this.previewErrors, seconds, npcId });
  }

  /** Free one slot's props/animators/HUD line and unload its Lua env. */
  private unloadSlot(slot: ScriptSlot): void {
    for (const p of slot.props.values()) this.disposeProp(p);
    slot.props.clear();
    for (const k of [...this.tweens.keys()]) if (k.startsWith(`${slot.id}:`)) this.tweens.delete(k);
    for (const k of [...this.spins.keys()]) if (k.startsWith(`${slot.id}:`)) this.spins.delete(k);
    for (const k of [...this.scaleTweens.keys()]) if (k.startsWith(`${slot.id}:`)) this.scaleTweens.delete(k);
    this.hudLines.delete(slot.rowKey);
    this.renderHud();
    this.runtime?.unloadScript(slot.id);
  }

  /** Unload every script + prop. `keepSig` preserves the just-computed signature. */
  private async teardownAll(keepSig = ''): Promise<void> {
    for (const slot of this.slots.values()) {
      for (const p of slot.props.values()) this.disposeProp(p);
      slot.props.clear();
      this.runtime?.unloadScript(slot.id);
    }
    this.slots.clear();
    this.npcSlotSig.clear();
    this.signalQueue.length = 0;
    this.tweens.clear();
    this.spins.clear();
    this.scaleTweens.clear();
    for (const b of this.bursts) this.disposeBurst(b);
    this.bursts.length = 0;
    this.eventQueue.length = 0;
    // Restore anything a script overrode on the shared scene.
    this.scriptFog = null;
    if (this.fogSaved) {
      this.scene.fog = this.fogSaved.had ? this.fogSaved.fog : null;
      this.fogSaved = null;
    }
    for (const pid of this.partOverrides) this.resetPartVisual(pid);
    this.partOverrides.clear();
    this.hudLines.clear();
    this.renderHud();
    this.worldSig = keepSig;
    if (!keepSig && this.runtime) {
      // Full world exit: drop the whole WASM instance (per-instance factory —
      // the engine's own multi-world guidance) rather than keeping it warm.
      this.runtime.dispose();
      this.runtime = null;
      this.runtimeReady = false;
    }
  }

  // ─── NPC creator slots (one per enabled npc_unit in the world) ───────────────

  /** Diff the enabled units' script signatures against the live slots: load new, reload changed, unload gone. */
  private async syncNpcSlots(): Promise<void> {
    const npcs = this.npcs;
    const wid = atlas.currentWorldId;
    if (!npcs || wid === null) return;
    const want = new Map<string, { sig: string; u: NpcUnit }>();
    for (const id of npcs.ids()) {
      const u = npcs.get(id);
      if (!u || !u.row.enabled || !u.row.scriptSrc.trim() || id === this.previewNpcId) continue;
      want.set(id, { sig: `${u.row.updatedAt.microsSinceUnixEpoch}`, u });
    }
    for (const [id, sig] of [...this.npcSlotSig]) {
      const w = want.get(id);
      if (w && w.sig === sig) continue;
      const slot = this.slots.get(`npc:${id}`);
      if (slot) { this.unloadSlot(slot); this.slots.delete(slot.rowKey); }
      this.npcSlotSig.delete(id);
    }
    for (const [id, w] of want) {
      if (this.npcSlotSig.has(id)) continue;
      this.npcSlotSig.set(id, w.sig);
      try {
        await this.ensureRuntime();
        if (atlas.currentWorldId !== wid) { this.npcSlotSig.delete(id); return; } // world hopped mid-load — teardown owns it
        await this.loadSlot(`npc:${id}`, `npc:${w.u.row.name}`, w.u.row.scriptSrc, id);
      } catch (e) {
        // A thrown init/load (not a Lua syntax error — those return false) used to pin the signature, so the NPC never retried.
        this.npcSlotSig.delete(id);
        this.lastSyncError = String((e as Error)?.message ?? e);
        Logger.warn('WorldScript', `npc slot ${id} load threw: ${this.lastSyncError}`);
        bus.emit('atlas:script-log', { script: `npc:${w.u.row.name}`, msg: `load threw: ${this.lastSyncError}`, level: 'error' });
      }
    }
  }

  /** The unit whose slot is executing (null inside a plain world script). */
  private activeUnit(): NpcUnit | null {
    const id = this.activeSlot?.npcId;
    return id ? this.npcs?.get(id) ?? null : null;
  }

  /** npc.signal — fan a message to every other script next frame (budgeted). */
  private signal(topic: unknown, payload: unknown): boolean {
    if (this.signalQueue.length >= MAX_SIGNALS_PER_FRAME) return false;
    const t = String(topic ?? '').slice(0, 64), p = String(payload ?? '').slice(0, 400);
    const from = `script:${this.activeSlot?.npcId ?? ''}`;
    this.signalQueue.push({ topic: t, payload: p, from });
    bus.emit('atlas:signal', { topic: t, payload: p, from }); // devices listen here
    return true;
  }

  // ─── Per-frame pump ───────────────────────────────────────────────────────────

  /** Nearest build part within reach of the player, or null. */
  private nearestPart(): { id: string; dist: number } | null {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return null;
    let best: { id: string; dist: number } | null = null;
    try {
      for (const p of conn.db.part.iter()) {
        if (p.worldId !== wid) continue;
        const d = Math.hypot(p.posX - atlas.localX, p.posZ - atlas.localZ);
        if (d <= INTERACT_RANGE_M && (!best || d < best.dist)) best = { id: p.partId.toString(), dist: d };
      }
    } catch { /* row cache mid-churn */ }
    return best;
  }

  private pendingInteract: { id: string; dist: number } | null = null;
  private queueInteract(): void {
    const near = this.nearestPart();
    if (near) this.pendingInteract = near;
  }

  private pump(dt: number): void {
    this.advanceAnimators(dt);
    // NPC creator slots follow the npc_unit rows (polled by signature — no
    // callback-order dependency on the host module's own subscription).
    // Sync the moment the host's signature changes (a placed/edited NPC starts its script on the NEXT frame — a
    // frame-counted cadence once cost 15+ s on a slow tab), with a wall-clock fallback for same-signature handbacks.
    if (this.npcs) {
      const now = performance.now();
      // The signature is a sorted spread with a BigInt comparator + template
      // strings: rebuild it only when a row changed (the host's version counter)
      // or on the wall-clock fallback — not every frame (2026-09-12).
      if (this.npcs.scriptVersion !== this.lastNpcVersion || now - this.lastNpcSyncAt > NPC_SYNC_FALLBACK_MS) {
        this.lastNpcVersion = this.npcs.scriptVersion;
        const sig = this.npcs.scriptSignature();
        if (sig !== this.lastNpcSig || now - this.lastNpcSyncAt > NPC_SYNC_FALLBACK_MS) { this.lastNpcSig = sig; this.lastNpcSyncAt = now; void this.syncNpcSlots(); }
      }
    }
    if (!this.runtime || !this.runtimeReady || this.slots.size === 0) return;
    const events = this.eventQueue.splice(0, this.eventQueue.length);
    const signals = this.signalQueue.splice(0, this.signalQueue.length);
    const interact = this.pendingInteract;
    this.pendingInteract = null;
    for (const slot of [...this.slots.values()]) {
      if (slot.strikes >= MAX_STRIKES) continue;
      const t0 = performance.now();
      this.activeSlot = slot;
      try {
        for (const ev of events) this.call(slot, 'on_event', ev.type, ev.payload);
        for (const sg of signals) {
          const fromNpc = sg.from.startsWith('script:') ? sg.from.slice(7) : '';
          if (fromNpc && fromNpc === slot.npcId) continue; // a script never hears its own broadcast
          this.call(slot, 'on_signal', sg.topic, sg.payload, sg.from.startsWith('script:') ? fromNpc : sg.from);
        }
        if (slot.npcId) {
          // The NPC's senses (proximity, E, talk, topics, arrivals, versona
          // replies, the hour) — queued by the host, delivered here in order.
          const u = this.npcs?.get(slot.npcId);
          if (u) for (const ev of u.drain()) this.call(slot, ev.hook, ...ev.args);
        } else if (interact) this.call(slot, 'on_interact', interact.id, interact.dist);
        this.call(slot, 'on_tick', dt);
      } finally {
        this.activeSlot = null;
      }
      // Soft watchdog: the engine's error budget catches THROWING scripts; this
      // catches merely-slow ones (a hard infinite loop can still hang the tab —
      // wasmoon is synchronous; documented limitation).
      if (performance.now() - t0 > MAX_PUMP_MS) {
        slot.strikes++;
        bus.emit('atlas:script-log', {
          script: slot.name, level: 'error',
          msg: `slow tick (> ${MAX_PUMP_MS} ms) — strike ${slot.strikes}/${MAX_STRIKES}`,
        });
        if (slot.strikes >= MAX_STRIKES) {
          bus.emit('atlas:status', { text: `World script "${slot.name}" disabled locally (too slow).` });
          bus.emit('atlas:script-log', { script: slot.name, msg: 'DISABLED locally (3 slow strikes)', level: 'error' });
          for (const p of slot.props.values()) this.disposeProp(p);
          slot.props.clear();
        }
      }
    }
  }

  // ─── Module-side animators (tweens + particle bursts) ────────────────────────

  private advanceAnimators(dt: number): void {
    for (const sp of this.spins.values()) sp.obj.rotation.y += sp.rate * dt;
    if (this.scaleTweens.size > 0) {
      const now = performance.now();
      for (const [k, st] of [...this.scaleTweens]) {
        const f = Math.min(1, (now - st.t0) / st.dur);
        st.obj.scale.setScalar(st.from + (st.to - st.from) * f);
        if (f >= 1) this.scaleTweens.delete(k);
      }
    }
    if (this.tweens.size > 0) {
      const now = performance.now();
      for (const [k, tw] of [...this.tweens]) {
        const f = Math.min(1, (now - tw.t0) / tw.dur);
        tw.obj.position.lerpVectors(tw.from, tw.to, f);
        if (f >= 1) this.tweens.delete(k);
      }
    }
    if (this.bursts.length > 0) {
      const now = performance.now();
      for (let i = this.bursts.length - 1; i >= 0; i--) {
        const b = this.bursts[i];
        const age = (now - b.born) / 1000;
        if (age > b.life) {
          this.disposeBurst(b);
          this.bursts.splice(i, 1);
          continue;
        }
        const pos = b.points.geometry.getAttribute('position') as THREE.BufferAttribute;
        const arr = pos.array as Float32Array;
        for (let j = 0; j < arr.length; j += 3) {
          arr[j] += b.vel[j] * dt;
          arr[j + 1] += b.vel[j + 1] * dt;
          arr[j + 2] += b.vel[j + 2] * dt;
          b.vel[j + 1] -= 4 * dt; // light gravity
        }
        pos.needsUpdate = true;
        (b.points.material as THREE.PointsMaterial).opacity = Math.max(0, 1 - age / b.life);
      }
    }
  }

  private disposeBurst(b: { points: THREE.Points }): void {
    this.root.remove(b.points);
    b.points.geometry.dispose();
    (b.points.material as THREE.Material).dispose();
  }

  // ─── v3 helpers (HUD overlay, part rows, camera, material cloning) ───────────

  private renderHud(): void {
    const lines = [...this.hudLines.values()].filter((l) => l.length > 0);
    if (lines.length === 0) { this.hudEl?.remove(); this.hudEl = null; return; }
    if (!this.hudEl) {
      this.hudEl = document.createElement('div');
      this.hudEl.style.cssText =
        'position:fixed;top:64px;left:12px;z-index:40;pointer-events:none;display:flex;flex-direction:column;gap:3px;' +
        'font:600 13px/1.4 var(--ax-font);color:var(--ax-text);text-shadow:0 1px 3px rgba(0,0,0,.9)';
      document.body.appendChild(this.hudEl);
    }
    this.hudEl.textContent = '';
    for (const l of lines) {
      const d = document.createElement('div');
      d.textContent = l;
      this.hudEl.appendChild(d);
    }
  }

  private partRow(id: string): { posX: number; posY: number; posZ: number; rotX: number; rotY: number; rotZ: number; rotW: number; scaleX: number; scaleY: number; scaleZ: number; primType: string; partId: bigint; worldId: bigint } | null {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return null;
    try {
      for (const p of conn.db.part.iter()) {
        if (p.worldId === wid && p.partId.toString() === id) return p;
      }
    } catch { /* pre-publish server */ }
    return null;
  }

  /** Re-apply the replicated transform after a local part_set override. */
  private resetPartVisual(id: string): void {
    const mesh = this.parts?.partMesh(id);
    const row = this.partRow(id);
    if (!mesh || !row) return;
    mesh.position.set(row.posX, row.posY, row.posZ);
    mesh.quaternion.set(row.rotX, row.rotY, row.rotZ, row.rotW);
    mesh.scale.set(row.scaleX, row.scaleY, row.scaleZ);
  }

  private camera(): THREE.PerspectiveCamera | null {
    return (atlas.localMesh?.children[0] as THREE.PerspectiveCamera | undefined) ?? null;
  }

  /** Clone materials once per prop so tinting never bleeds into shared assets. */
  private ownMaterials(obj: THREE.Object3D): void {
    obj.traverse((o) => {
      const m = o as THREE.Mesh & { material?: THREE.Material | THREE.Material[] };
      if (!m.isMesh || !m.material) return;
      if ((m.userData as { wsOwnMat?: boolean }).wsOwnMat) return;
      m.material = Array.isArray(m.material) ? m.material.map((mm) => mm.clone()) : m.material.clone();
      (m.userData as { wsOwnMat?: boolean }).wsOwnMat = true;
    });
  }

  private storeKey(): string { return `atlas:ws:${atlas.currentWorldId ?? 0}`; }

  private readStore(): Record<string, string | number> {
    try { return JSON.parse(localStorage.getItem(this.storeKey()) ?? '{}') as Record<string, string | number>; } catch { return {}; }
  }

  // ─── The curated bridge ───────────────────────────────────────────────────────

  private installBridge(rt: LuaRuntime): void {
    const spawn = (prefabKey: unknown, x: unknown, y: unknown, z: unknown): number => {
      const slot = this.activeSlot;
      const conn = atlas.conn;
      if (!slot || !conn || typeof prefabKey !== 'string') return 0;
      if (slot.props.size >= MAX_PROPS_PER_SCRIPT) return 0;
      const h = slot.nextHandle++;
      const holder = new THREE.Group();
      holder.position.set(Number(x) || 0, Number(y) || 0, Number(z) || 0);
      this.root.add(holder);
      slot.props.set(h, { obj: holder, assetId: null });
      // Resolve prefab → cooked render asset; placeholder box when unregistered.
      const pm = [...conn.db.prefabModel.iter()].find((p) => p.prefabKey === prefabKey);
      if (!pm || pm.assetId === 0n) {
        const box = new THREE.Mesh(
          new THREE.BoxGeometry(1, 1, 1),
          new THREE.MeshStandardMaterial({ color: 0x88ccff, wireframe: true }),
        );
        holder.add(box);
        return h;
      }
      void this.assets.load(pm.assetId).then((tmpl) => {
        const rec = slot.props.get(h);
        if (!rec) { this.assets.dispose(pm.assetId); return; } // removed mid-load
        rec.assetId = pm.assetId;
        rec.obj.add(tmpl.clone());
      });
      return h;
    };

    const set = (h: unknown, x: unknown, y: unknown, z: unknown, yaw?: unknown, scale?: unknown): boolean => {
      const rec = this.activeSlot?.props.get(Number(h));
      if (!rec) return false;
      rec.obj.position.set(Number(x) || 0, Number(y) || 0, Number(z) || 0);
      if (yaw !== undefined && yaw !== null) rec.obj.rotation.y = Number(yaw) || 0;
      if (scale !== undefined && scale !== null) {
        const s = Math.min(50, Math.max(0.01, Number(scale) || 1));
        rec.obj.scale.setScalar(s);
      }
      return true;
    };

    const remove = (h: unknown): boolean => {
      const slot = this.activeSlot;
      const rec = slot?.props.get(Number(h));
      if (!slot || !rec) return false;
      if ((rec.obj as THREE.PointLight).isPointLight) slot.lights = Math.max(0, slot.lights - 1);
      this.spins.delete(`${slot.id}:${Number(h)}`);
      this.scaleTweens.delete(`${slot.id}:${Number(h)}`);
      this.tweens.delete(`${slot.id}:${Number(h)}`);
      this.disposeProp(rec);
      slot.props.delete(Number(h));
      return true;
    };

    const label = (text: unknown, x: unknown, y: unknown, z: unknown): number => {
      const slot = this.activeSlot;
      if (!slot || slot.props.size >= MAX_PROPS_PER_SCRIPT) return 0;
      const canvas = document.createElement('canvas');
      canvas.width = 512; canvas.height = 128;
      const g = canvas.getContext('2d')!;
      g.font = '600 44px system-ui';
      g.fillStyle = '#eaf6ff';
      g.textAlign = 'center';
      g.strokeStyle = 'rgba(0,0,0,.8)';
      g.lineWidth = 6;
      const s = String(text).slice(0, 48);
      g.strokeText(s, 256, 78);
      g.fillText(s, 256, 78);
      const tex = new THREE.CanvasTexture(canvas);
      const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false }));
      sprite.scale.set(6, 1.5, 1);
      sprite.position.set(Number(x) || 0, Number(y) || 0, Number(z) || 0);
      this.root.add(sprite);
      const h = slot.nextHandle++;
      slot.props.set(h, { obj: sprite, assetId: null });
      return h;
    };

    // Players: FLAT accessors (wasmoon collapses JS objects — the bridge rule).
    const playerRows = (): Array<{ name: string; x: number; y: number; z: number }> => {
      const conn = atlas.conn;
      const wid = atlas.currentWorldId;
      if (!conn || wid === null) return [];
      const out: Array<{ name: string; x: number; y: number; z: number }> = [];
      for (const p of conn.db.playerState.iter()) {
        if (p.worldId !== wid) continue;
        let name = '';
        for (const a of conn.db.account.iter()) {
          if (a.identity.toHexString() === p.identity.toHexString()) { name = a.username; break; }
        }
        out.push({ name, x: p.posX, y: p.posY, z: p.posZ });
      }
      return out;
    };

    const tween = (h: unknown, x: unknown, y: unknown, z: unknown, seconds: unknown): boolean => {
      const slot = this.activeSlot;
      const rec = slot?.props.get(Number(h));
      if (!slot || !rec) return false;
      const dur = Math.min(60, Math.max(0.05, Number(seconds) || 1)) * 1000;
      this.tweens.set(`${slot.id}:${Number(h)}`, {
        obj: rec.obj,
        from: rec.obj.position.clone(),
        to: new THREE.Vector3(Number(x) || 0, Number(y) || 0, Number(z) || 0),
        t0: performance.now(),
        dur,
      });
      return true;
    };

    const particles = (x: unknown, y: unknown, z: unknown, count?: unknown, hexColor?: unknown, speed?: unknown): boolean => {
      const slot = this.activeSlot;
      if (!slot) return false;
      if (this.bursts.filter((b) => b.slot === slot).length >= MAX_BURSTS_PER_SCRIPT) return false;
      const n = Math.min(MAX_BURST_PARTICLES, Math.max(1, Math.floor(Number(count) || 60)));
      const spd = Math.min(20, Math.max(0.1, Number(speed) || 4));
      const posArr = new Float32Array(n * 3);
      const vel = new Float32Array(n * 3);
      for (let i = 0; i < n; i++) {
        posArr[i * 3] = Number(x) || 0;
        posArr[i * 3 + 1] = Number(y) || 0;
        posArr[i * 3 + 2] = Number(z) || 0;
        // Deterministic-ish fan: golden-angle sphere scatter (no Math.random —
        // identical bursts on every client keep worlds looking the same).
        const a = i * 2.399963;
        const v = 1 - (2 * (i + 0.5)) / n;
        const r = Math.sqrt(1 - v * v);
        vel[i * 3] = Math.cos(a) * r * spd;
        vel[i * 3 + 1] = Math.abs(v) * spd;
        vel[i * 3 + 2] = Math.sin(a) * r * spd;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(posArr, 3));
      let color = 0xffd27f;
      if (typeof hexColor === 'string') {
        const parsed = parseInt(hexColor.replace('#', ''), 16);
        if (Number.isFinite(parsed)) color = parsed;
      } else if (typeof hexColor === 'number' && Number.isFinite(hexColor)) color = hexColor;
      const mat = new THREE.PointsMaterial({ color, size: 0.12, transparent: true, depthWrite: false });
      const points = new THREE.Points(geo, mat);
      this.root.add(points);
      this.bursts.push({ points, vel, born: performance.now(), life: 1.6, slot });
      return true;
    };

    const tone = (freq: unknown, ms: unknown, vol?: unknown): boolean => {
      if (this.liveTones >= MAX_TONES) return false;
      if (isGameAudioMuted()) return false; // the intro video silences the game (a script can tell: the call returns false)
      try {
        // THE TONE RIDES THE SHARED MIX (2026-09-20). It used to own a private AudioContext wired STRAIGHT to the
        // destination: the "refused while muted" check above stops a NEW tone, but a tone already sounding when the
        // intro began kept ringing through the film for up to 2 s, outside the mute switch, the master volume and
        // the limiter. On the machinery bus it is behind the game gate like everything else — continuously.
        const rt = audioRuntime();
        const ctx = rt.ctx, out = rt.busInput('machinery');
        if (!ctx || !out || ctx.state !== 'running') return false; // no gesture yet / suspended: a script learns it did not sound
        const f = Math.min(4000, Math.max(40, Number(freq) || 440));
        const dur = Math.min(2000, Math.max(20, Number(ms) || 150)) / 1000;
        const v = Math.min(0.2, Math.max(0.005, Number(vol) || 0.08));
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = f;
        gain.gain.setValueAtTime(v, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
        osc.connect(gain).connect(out);
        this.liveTones++;
        osc.onended = () => { this.liveTones--; try { osc.disconnect(); gain.disconnect(); } catch { /* torn down */ } };
        osc.start();
        osc.stop(ctx.currentTime + dur);
        return true;
      } catch { return false; }
    };

    // ── v3: primitives + lights ─────────────────────────────────────────────────
    const registerProp = (obj: THREE.Object3D): number => {
      const slot = this.activeSlot!;
      const h = slot.nextHandle++;
      this.root.add(obj);
      slot.props.set(h, { obj, assetId: null });
      return h;
    };

    const parseHex = (hex: unknown, fallback: number): number => {
      if (typeof hex === 'string') {
        const p = parseInt(hex.replace('#', ''), 16);
        if (Number.isFinite(p)) return p;
      } else if (typeof hex === 'number' && Number.isFinite(hex)) return hex;
      return fallback;
    };

    const box = (x: unknown, y: unknown, z: unknown, sx?: unknown, sy?: unknown, sz?: unknown, hex?: unknown): number => {
      const slot = this.activeSlot;
      if (!slot || slot.props.size >= MAX_PROPS_PER_SCRIPT) return 0;
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(Math.max(0.01, Number(sx) || 1), Math.max(0.01, Number(sy) || 1), Math.max(0.01, Number(sz) || 1)),
        new THREE.MeshStandardMaterial({ color: parseHex(hex, 0x9ec5ff), roughness: 0.55 }),
      );
      mesh.position.set(Number(x) || 0, Number(y) || 0, Number(z) || 0);
      return registerProp(mesh);
    };

    const sphere = (x: unknown, y: unknown, z: unknown, r?: unknown, hex?: unknown): number => {
      const slot = this.activeSlot;
      if (!slot || slot.props.size >= MAX_PROPS_PER_SCRIPT) return 0;
      const mesh = new THREE.Mesh(
        new THREE.SphereGeometry(Math.min(50, Math.max(0.01, Number(r) || 0.5)), 20, 14),
        new THREE.MeshStandardMaterial({ color: parseHex(hex, 0x9ec5ff), roughness: 0.55 }),
      );
      mesh.position.set(Number(x) || 0, Number(y) || 0, Number(z) || 0);
      return registerProp(mesh);
    };

    const light = (x: unknown, y: unknown, z: unknown, hex?: unknown, intensity?: unknown, range?: unknown): number => {
      const slot = this.activeSlot;
      if (!slot || slot.props.size >= MAX_PROPS_PER_SCRIPT || slot.lights >= MAX_LIGHTS_PER_SCRIPT) return 0;
      const pl = new THREE.PointLight(
        parseHex(hex, 0xffd9a0),
        Math.min(200, Math.max(0.05, Number(intensity) || 30)),
        Math.min(120, Math.max(0.5, Number(range) || 24)),
        2,
      );
      pl.position.set(Number(x) || 0, Number(y) || 0, Number(z) || 0);
      slot.lights++;
      return registerProp(pl);
    };

    // ── v3: per-prop FX ─────────────────────────────────────────────────────────
    const color = (h: unknown, hex: unknown): boolean => {
      const rec = this.activeSlot?.props.get(Number(h));
      if (!rec) return false;
      const c = parseHex(hex, 0xffffff);
      this.ownMaterials(rec.obj);
      rec.obj.traverse((o) => {
        const m = o as THREE.Mesh & { material?: THREE.Material | THREE.Material[] };
        if (!m.isMesh || !m.material) return;
        for (const mm of Array.isArray(m.material) ? m.material : [m.material]) {
          (mm as THREE.MeshStandardMaterial).color?.setHex(c);
        }
      });
      if ((rec.obj as THREE.PointLight).isPointLight) (rec.obj as THREE.PointLight).color.setHex(c);
      return true;
    };

    const opacity = (h: unknown, a: unknown): boolean => {
      const rec = this.activeSlot?.props.get(Number(h));
      if (!rec) return false;
      const v = Math.min(1, Math.max(0, Number(a) ?? 1));
      this.ownMaterials(rec.obj);
      rec.obj.traverse((o) => {
        const m = o as THREE.Mesh & { material?: THREE.Material | THREE.Material[] };
        if (!m.isMesh || !m.material) return;
        for (const mm of Array.isArray(m.material) ? m.material : [m.material]) {
          mm.transparent = v < 1;
          mm.opacity = v;
        }
      });
      return true;
    };

    const spin = (h: unknown, radPerSec: unknown): boolean => {
      const slot = this.activeSlot;
      const rec = slot?.props.get(Number(h));
      if (!slot || !rec) return false;
      const rate = Math.min(20, Math.max(-20, Number(radPerSec) || 0));
      const key = `${slot.id}:${Number(h)}`;
      if (rate === 0) this.spins.delete(key);
      else this.spins.set(key, { obj: rec.obj, rate });
      return true;
    };

    const scaleTo = (h: unknown, s: unknown, seconds: unknown): boolean => {
      const slot = this.activeSlot;
      const rec = slot?.props.get(Number(h));
      if (!slot || !rec) return false;
      this.scaleTweens.set(`${slot.id}:${Number(h)}`, {
        obj: rec.obj,
        from: rec.obj.scale.x,
        to: Math.min(50, Math.max(0.01, Number(s) || 1)),
        t0: performance.now(),
        dur: Math.min(60, Math.max(0.05, Number(seconds) || 1)) * 1000,
      });
      return true;
    };

    const lookAt = (h: unknown, x: unknown, y: unknown, z: unknown): boolean => {
      const rec = this.activeSlot?.props.get(Number(h));
      if (!rec) return false;
      // The script's target is in the world's own coordinates (the prop's PARENT frame); three aims from the prop's WORLD
      // position, which carries the rebase shift — past the first rebase every scripted prop turned toward the origin.
      lookAtLocal(rec.obj, LOOK_TARGET.set(Number(x) || 0, Number(y) || 0, Number(z) || 0));
      return true;
    };

    const attach = (h: unknown, partId: unknown): boolean => {
      const rec = this.activeSlot?.props.get(Number(h));
      const mesh = this.parts?.partMesh(String(partId));
      if (!rec || !mesh) return false;
      mesh.attach(rec.obj); // keeps the world transform; follows the part afterwards
      return true;
    };

    const detach = (h: unknown): boolean => {
      const rec = this.activeSlot?.props.get(Number(h));
      if (!rec) return false;
      this.root.attach(rec.obj);
      return true;
    };

    // ── v3: environment ─────────────────────────────────────────────────────────
    const fog = (hex: unknown, density: unknown): boolean => {
      if (!this.fogSaved) this.fogSaved = { fog: this.scene.fog, had: this.scene.fog !== null };
      const d = Math.min(0.2, Math.max(0.00005, Number(density) || 0.004));
      const color = parseHex(hex, 0x9db6cc);
      // MUTATE one owned FogExp2 — swapping the scene.fog OBJECT forces a full
      // material recompile (the AtmosphereModule fog law), and nothing stops a
      // script calling set_fog from on_tick: the swap made that freeze-class.
      if (this.scriptFog && this.scene.fog === this.scriptFog) {
        if (this.scriptFog.color.getHex() !== color) this.scriptFog.color.setHex(color);
        this.scriptFog.density = d;
      } else {
        this.scriptFog = new THREE.FogExp2(color, d);
        this.scene.fog = this.scriptFog; // one swap on first use (unavoidable)
      }
      return true;
    };

    const fogClear = (): boolean => {
      if (this.fogSaved) {
        this.scene.fog = this.fogSaved.had ? this.fogSaved.fog : null;
        this.fogSaved = null;
        this.scriptFog = null;
      }
      return true;
    };

    // ── v3: queries ─────────────────────────────────────────────────────────────
    const raycast = (x: unknown, y: unknown, z: unknown, dx: unknown, dy: unknown, dz: unknown): number => {
      // Scratch vectors: scripts may raycast every on_tick.
      const dir = RAY_DIR.set(Number(dx) || 0, Number(dy) || 0, Number(dz) || 0);
      if (dir.lengthSq() < 1e-9) return -1;
      this.raycaster.set(RAY_ORIGIN.set(Number(x) || 0, Number(y) || 0, Number(z) || 0), dir.normalize());
      this.raycaster.far = 500;
      const targets: THREE.Object3D[] = [this.root];
      if (this.parts?.root) targets.push(this.parts.root);
      const hits = this.raycaster.intersectObjects(targets, true);
      return hits.length > 0 ? hits[0].distance : -1;
    };

    const partIds = (): string[] => {
      const conn = atlas.conn;
      const wid = atlas.currentWorldId;
      if (!conn || wid === null) return [];
      try {
        return [...conn.db.part.iter()].filter((p) => p.worldId === wid)
          .sort((a, b) => (a.partId < b.partId ? -1 : 1)).map((p) => p.partId.toString());
      } catch { return []; }
    };

    const partSet = (id: unknown, x: unknown, y: unknown, z: unknown, yaw?: unknown): boolean => {
      const pid = String(id);
      const mesh = this.parts?.partMesh(pid);
      if (!mesh) return false;
      mesh.position.set(Number(x) || 0, Number(y) || 0, Number(z) || 0);
      if (yaw !== undefined && yaw !== null) mesh.rotation.set(0, Number(yaw) || 0, 0);
      this.partOverrides.add(pid);
      return true;
    };

    const partReset = (id: unknown): boolean => {
      const pid = String(id);
      if (!this.partOverrides.has(pid)) return false;
      this.resetPartVisual(pid);
      this.partOverrides.delete(pid);
      return true;
    };

    const npcRows = (): Array<{ archetype: string; x: number; z: number; state: string }> => {
      const conn = atlas.conn;
      const wid = atlas.currentWorldId;
      if (!conn || wid === null) return [];
      try {
        const out: Array<{ archetype: string; x: number; z: number; state: string }> = [];
        for (const v of conn.db.versona.iter()) {
          if (v.worldId === wid) out.push({ archetype: v.archetype, x: v.curX, z: v.curZ, state: v.state });
        }
        return out;
      } catch { return []; }
    };

    // ── v3: UI + storage ────────────────────────────────────────────────────────
    const hud = (text: unknown): boolean => {
      const slot = this.activeSlot;
      if (!slot) return false;
      this.hudLines.set(slot.rowKey, String(text ?? '').slice(0, 200));
      this.renderHud();
      return true;
    };

    const toast = (msg: unknown): boolean => {
      const slot = this.activeSlot;
      if (!slot) return false;
      const now = performance.now();
      if (now - slot.lastToastAt < 2000) return false; // rate limit: 1 toast / 2 s / script
      slot.lastToastAt = now;
      bus.emit('atlas:status', { text: String(msg ?? '').slice(0, 160) });
      return true;
    };

    const storeSet = (key: unknown, val: unknown): boolean => {
      const k = String(key ?? '').slice(0, 64);
      if (!k) return false;
      const store = this.readStore();
      if (!(k in store) && Object.keys(store).length >= MAX_STORE_KEYS) return false;
      store[k] = typeof val === 'number' && Number.isFinite(val) ? val : String(val ?? '').slice(0, MAX_STORE_VALUE);
      try { localStorage.setItem(this.storeKey(), JSON.stringify(store)); return true; } catch { return false; }
    };

    const storeGet = (key: unknown): string | number => {
      const v = this.readStore()[String(key ?? '')];
      return v === undefined ? '' : v;
    };

    rt.registerGlobal('world', {
      spawn,
      set,
      remove,
      label,
      tween,
      particles,
      tone,
      // v3 — primitives + lights + FX
      box,
      sphere,
      light,
      color,
      opacity,
      spin,
      scale_to: scaleTo,
      look_at: lookAt,
      attach,
      detach,
      // v3 — environment
      fog,
      fog_clear: fogClear,
      // v3 — queries
      raycast,
      dist: (x1: unknown, y1: unknown, z1: unknown, x2: unknown, y2: unknown, z2: unknown) =>
        Math.hypot((Number(x2) || 0) - (Number(x1) || 0), (Number(y2) || 0) - (Number(y1) || 0), (Number(z2) || 0) - (Number(z1) || 0)),
      part_count: () => partIds().length,
      part_id: (i: unknown) => partIds()[Number(i) - 1] ?? '',
      part_x: (id: unknown) => this.partRow(String(id))?.posX ?? 0,
      part_y: (id: unknown) => this.partRow(String(id))?.posY ?? 0,
      part_z: (id: unknown) => this.partRow(String(id))?.posZ ?? 0,
      part_yaw: (id: unknown) => {
        const r = this.partRow(String(id));
        if (!r) return 0;
        return new THREE.Euler().setFromQuaternion(new THREE.Quaternion(r.rotX, r.rotY, r.rotZ, r.rotW), 'YXZ').y;
      },
      part_type: (id: unknown) => this.partRow(String(id))?.primType ?? '',
      part_set: partSet,
      part_reset: partReset,
      npc_count: () => npcRows().length,
      npc_name: (i: unknown) => npcRows()[Number(i) - 1]?.archetype ?? '',
      npc_x: (i: unknown) => npcRows()[Number(i) - 1]?.x ?? 0,
      npc_z: (i: unknown) => npcRows()[Number(i) - 1]?.z ?? 0,
      npc_state: (i: unknown) => npcRows()[Number(i) - 1]?.state ?? '',
      cam_x: () => this.camera()?.getWorldPosition(new THREE.Vector3()).x ?? 0,
      cam_y: () => this.camera()?.getWorldPosition(new THREE.Vector3()).y ?? 0,
      cam_z: () => this.camera()?.getWorldPosition(new THREE.Vector3()).z ?? 0,
      cam_yaw: () => {
        const c = this.camera();
        if (!c) return 0;
        return new THREE.Euler().setFromQuaternion(c.getWorldQuaternion(new THREE.Quaternion()), 'YXZ').y;
      },
      cam_pitch: () => {
        const c = this.camera();
        if (!c) return 0;
        return new THREE.Euler().setFromQuaternion(c.getWorldQuaternion(new THREE.Quaternion()), 'YXZ').x;
      },
      key_down: (key: unknown) => this.keys.has(String(key ?? '').toLowerCase()),
      is_owner: () => {
        const conn = atlas.conn;
        const wid = atlas.currentWorldId;
        if (!conn || wid === null || !atlas.identityHex) return false;
        return conn.db.world.worldId.find(wid)?.owner.toHexString() === atlas.identityHex;
      },
      world_name: () => {
        const conn = atlas.conn;
        const wid = atlas.currentWorldId;
        if (!conn || wid === null) return '';
        return conn.db.world.worldId.find(wid)?.name ?? '';
      },
      world_size: () => {
        const conn = atlas.conn;
        const wid = atlas.currentWorldId;
        if (!conn || wid === null) return 0;
        return conn.db.world.worldId.find(wid)?.sizeM ?? 0;
      },
      time: () => (performance.now() - this.bornAt) / 1000,
      // v3 — UI + storage
      hud,
      toast,
      store_set: storeSet,
      store_get: storeGet,
      // devices, signals & cinematics (Edit Mode devices)
      signal: (t: unknown, p: unknown) => this.signal(t, p),
      device_count: () => this.devices?.ids().length ?? 0,
      device_id: (i: unknown) => this.devices?.ids()[Number(i) - 1] ?? '',
      device_name: (id: unknown) => this.devices?.rowByRef(String(id ?? ''))?.name ?? '',
      device_kind: (id: unknown) => this.devices?.rowByRef(String(id ?? ''))?.kind ?? '',
      device_x: (id: unknown) => this.devices?.posByRef(String(id ?? ''))?.x ?? 0,
      device_y: (id: unknown) => this.devices?.posByRef(String(id ?? ''))?.y ?? 0,
      device_z: (id: unknown) => this.devices?.posByRef(String(id ?? ''))?.z ?? 0,
      device_state: (id: unknown) => this.devices?.stateByRef(String(id ?? '')) ?? '',
      device_trigger: (id: unknown) => this.devices?.trigger(String(id ?? '')) ?? false,
      play_cinematic: (name: unknown) => this.devices?.playCinematic(String(name ?? '')) ?? false,
      stop_cinematic: () => { this.devices?.stopCinematic(); return true; },
      my_name: () => {
        const conn = atlas.conn;
        if (!conn || !atlas.identityHex) return '';
        for (const a of conn.db.account.iter()) {
          if (a.identity.toHexString() === atlas.identityHex) return a.username;
        }
        return '';
      },
      my_x: () => atlas.localX,
      my_y: () => atlas.localY,
      my_z: () => atlas.localZ,
      player_count: () => playerRows().length,
      player_name: (i: unknown) => playerRows()[Number(i) - 1]?.name ?? '',
      player_x: (i: unknown) => playerRows()[Number(i) - 1]?.x ?? 0,
      player_y: (i: unknown) => playerRows()[Number(i) - 1]?.y ?? 0,
      player_z: (i: unknown) => playerRows()[Number(i) - 1]?.z ?? 0,
      terrain_height: (x: unknown, z: unknown) => (this.seed ? terrainHeight(this.seed, Number(x) || 0, Number(z) || 0) : 0),
      time_of_day: () => {
        const conn = atlas.conn;
        const wid = atlas.currentWorldId;
        if (!conn || wid === null) return 0.5;
        return conn.db.skyState.worldId.find(wid)?.timeOfDay ?? 0.5;
      },
      log: (msg: unknown) => {
        const script = this.activeSlot?.name ?? '?';
        const text = String(msg).slice(0, 300);
        console.info(`[world-script:${script}]`, text);
        bus.emit('atlas:script-log', { script, msg: text, level: 'info' }); // → Lua IDE console
      },
    });
    // The NPC creator API — bound per executing slot (a plain world script
    // gets harmless defaults). Built by the host so the senses and the Lua
    // surface live in one place; lua_api.ts is checked against it at build.
    if (this.npcs) rt.registerGlobal('npc', this.npcs.buildApi(() => this.activeUnit(), (t, p) => this.signal(t, p)));
  }

  private disposeProp(p: { obj: THREE.Object3D; assetId: bigint | null }): void {
    this.root.remove(p.obj);
    if (p.assetId) this.assets.dispose(p.assetId);
    else {
      p.obj.traverse((o) => { // deferred — a script may remove() inside its tick
        const m = o as THREE.Mesh & { material?: THREE.Material & { map?: THREE.Texture } };
        if (m.geometry) disposeLater(m.geometry);
        if (m.material) { if (m.material.map) disposeLater(m.material.map); disposeLater(m.material); }
      });
    }
  }

  dispose(): void {
    void this.teardownAll();
    this.scene.remove(this.root);
  }
}
